import {
  Component,
  ElementRef,
  Input,
  Output,
  OnInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
  ChangeDetectorRef,
  booleanAttribute
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GlobalEventListenerService } from 'src/app/modules/core/services/global-event-listener.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';

@Component({
  selector: 'topo-date-time-picker',
  templateUrl: './date-time-picker.component.html',
  styleUrls: ['./date-time-picker.component.scss'],
  imports: [CommonModule, FormsModule, TranslatePipe, FlexLayoutModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DateTimePickerComponent
    }
  ]
})
export class DateTimePickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() set value(value: any) {
    if (!!value) {
      this._value = new Date(value);
      this.setCurrentCalendarDateFromValue(this._value);
      this.setSelectedCalendarDateFromValue(this._value);
      this.buildDateMatrix();
      this.selectedYear = this._value.getFullYear();
      let hours = this._value.getHours();
      if (hours > 12) {
        this.amPmHour = hours - 12;
      } else if (hours === 0) {
        this.amPmHour = 12;
      } else {
        this.amPmHour = hours;
      }
      this.minutes = this._value.getMinutes();
    } else {
      this._value = null;
    }
  }
  get value(): any {
    return this._value;
  }

  get placeholder(): string {
    return this.showTime
      ? this.dateTimeFormats[this.formatIndex].dateTimePlaceholder
      : this.dateTimeFormats[this.formatIndex].datePlaceholder;
  }

  get mask(): string {
    return this.showTime ? this.dateTimeFormats[this.formatIndex].dateTimeMask : this.dateTimeFormats[this.formatIndex].dateMask;
  }

  constructor(
    private eventListenerService: GlobalEventListenerService,
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  @Output() valueChanged = new EventEmitter<Date>();
  @Input() showInput = true;
  @Input() disabled = false;
  @Input() showTime: boolean;
  @Input() allowEmptyDateString: boolean;
  @Input() class: string;
  @Input({ transform: booleanAttribute }) hasExternalTrigger: boolean;
  @Input() isYearDropdown: boolean;
  @Input() startYear: number = 1900;
  @ViewChild('dateTimeInput') dateTimeInput: ElementRef;
  @ViewChild('calendarPopup') calendarPopup: ElementRef;
  destroy$ = new Subject<void>();
  _value: Date;
  calendarInputValue: string;
  currentCalendarDate: Date;
  selectedCalendarDate: Date;
  touched = false;
  dateMatrix = [];
  showCalendar: boolean;
  day: number;
  monthDisplay: string;
  yearDisplay: number;
  amPmHour: number;
  minutes: number;

  todayDate = new Date(new Date().setHours(0, 0, 0, 0));
  formatIndex = 0;
  dateTimeFormats =
    this.translateService.currentLang === 'en-US'
      ? [
          {
            datePlaceholder: 'MM/DD/YYYY',
            dateMask: 'M0/d0/0000',
            dateTimePlaceholder: 'MM/DD/YYYY HH:MM:AM',
            dateTimeMask: 'M0/d0/0000 Hh:m0 SS'
          }
        ]
      : [
          {
            datePlaceholder: this.translateService.instant('FORMAT.DATE-ONLY'),
            dateMask: this.translateService.instant('FORMAT.DATE-ONLY'),
            dateTimePlaceholder: this.translateService.instant('FORMAT.LONG-DATE-WITH-TIME'),
            dateTimeMask: this.translateService.instant('FORMAT.LONG-DATE-WITH-TIME')
          }
        ];
  monthLabels = [
    'CALENDAR.MONTH-0',
    'CALENDAR.MONTH-1',
    'CALENDAR.MONTH-2',
    'CALENDAR.MONTH-3',
    'CALENDAR.MONTH-4',
    'CALENDAR.MONTH-5',
    'CALENDAR.MONTH-6',
    'CALENDAR.MONTH-7',
    'CALENDAR.MONTH-8',
    'CALENDAR.MONTH-9',
    'CALENDAR.MONTH-10',
    'CALENDAR.MONTH-11'
  ];
  dayLabels = [
    'CALENDAR.DAY-0',
    'CALENDAR.DAY-1',
    'CALENDAR.DAY-2',
    'CALENDAR.DAY-3',
    'CALENDAR.DAY-4',
    'CALENDAR.DAY-5',
    'CALENDAR.DAY-6'
  ];
  onChange = value => {};
  onTouched = () => {};
  lastClickEvent = 0;
  ignoreClick = false;
  hourValues: { value: number; display: string }[];
  twelveHourClock: { value: number; display: string }[] = [
    { value: 1, display: '01' },
    { value: 2, display: '02' },
    { value: 3, display: '03' },
    { value: 4, display: '04' },
    { value: 5, display: '05' },
    { value: 6, display: '06' },
    { value: 7, display: '07' },
    { value: 8, display: '08' },
    { value: 9, display: '09' },
    { value: 10, display: '10' },
    { value: 11, display: '11' },
    { value: 12, display: '12' }
  ];

  twentyFourHourClock: { value: number; display: string }[] = [
    { value: 0, display: '00' },
    { value: 1, display: '01' },
    { value: 2, display: '02' },
    { value: 3, display: '03' },
    { value: 4, display: '04' },
    { value: 5, display: '05' },
    { value: 6, display: '06' },
    { value: 7, display: '07' },
    { value: 8, display: '08' },
    { value: 9, display: '09' },
    { value: 10, display: '10' },
    { value: 11, display: '11' },
    { value: 12, display: '12' },
    { value: 13, display: '13' },
    { value: 14, display: '14' },
    { value: 15, display: '15' },
    { value: 16, display: '16' },
    { value: 17, display: '17' },
    { value: 18, display: '18' },
    { value: 19, display: '19' },
    { value: 20, display: '20' },
    { value: 21, display: '21' },
    { value: 22, display: '22' },
    { value: 23, display: '23' }
  ];

  minuteValues: { value: number; display: string }[] = [
    { value: 0, display: '00' },
    { value: 1, display: '01' },
    { value: 2, display: '02' },
    { value: 3, display: '03' },
    { value: 4, display: '04' },
    { value: 5, display: '05' },
    { value: 6, display: '06' },
    { value: 7, display: '07' },
    { value: 8, display: '08' },
    { value: 9, display: '09' },
    { value: 10, display: '10' },
    { value: 11, display: '11' },
    { value: 12, display: '12' },
    { value: 13, display: '13' },
    { value: 14, display: '14' },
    { value: 15, display: '15' },
    { value: 16, display: '16' },
    { value: 17, display: '17' },
    { value: 18, display: '18' },
    { value: 19, display: '19' },
    { value: 20, display: '20' },
    { value: 21, display: '21' },
    { value: 22, display: '22' },
    { value: 23, display: '23' },
    { value: 24, display: '24' },
    { value: 25, display: '25' },
    { value: 26, display: '26' },
    { value: 27, display: '27' },
    { value: 28, display: '28' },
    { value: 29, display: '29' },
    { value: 30, display: '30' },
    { value: 31, display: '31' },
    { value: 32, display: '32' },
    { value: 33, display: '33' },
    { value: 34, display: '34' },
    { value: 35, display: '35' },
    { value: 36, display: '36' },
    { value: 37, display: '37' },
    { value: 38, display: '38' },
    { value: 39, display: '39' },
    { value: 40, display: '40' },
    { value: 41, display: '41' },
    { value: 42, display: '42' },
    { value: 43, display: '43' },
    { value: 44, display: '44' },
    { value: 45, display: '45' },
    { value: 46, display: '46' },
    { value: 47, display: '47' },
    { value: 48, display: '48' },
    { value: 49, display: '49' },
    { value: 50, display: '50' },
    { value: 51, display: '51' },
    { value: 52, display: '52' },
    { value: 53, display: '53' },
    { value: 54, display: '54' },
    { value: 55, display: '55' },
    { value: 56, display: '56' },
    { value: 57, display: '57' },
    { value: 58, display: '58' },
    { value: 59, display: '59' }
  ];

  yearOptions: number[];
  selectedYear: number;

  ngOnInit(): void {
    this.setCurrentCalendarDateFromValue(this.value || new Date());

    this.buildDateMatrix();

    this.hourValues = this.twelveHourClock;
    this.yearOptions = Array.from({ length: new Date().getFullYear() - this.startYear + 1 }, (_, i) => this.startYear + i).reverse();

    // listen for click events. if the user clicks outside the calendar
    // and the calendar is open then toggle the display.
    this.eventListenerService.clickEvents$
      .pipe(
        filter(() => this.showCalendar),
        filter(target => !!this.calendarPopup && !this.calendarPopup.nativeElement.contains(target)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.lastClickEvent > new Date().getTime() - 300 || this.ignoreClick) {
          this.ignoreClick = false;
          return;
        }

        this.lastClickEvent = new Date().getTime();
        this.showCalendar = false;
        this.changeDetector.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  calendarClick(): void {
    if (!this._value) {
      this.value = new Date().getTime();
    }

    this.showCalendar = !this.showCalendar;
    this.lastClickEvent = new Date().getTime();
    this.changeDetector.markForCheck();

    if (this.showCalendar && this.isYearDropdown) {
      setTimeout(() => {
        const popupEl = this.calendarPopup?.nativeElement as HTMLDivElement;
        const dropdownEl = popupEl.querySelector('.year-dropdown') as HTMLDivElement;
        const optionEl = dropdownEl.querySelector(`#year_${this._value.getFullYear().toString()}`) as HTMLDivElement;

        if (!!optionEl) {
          dropdownEl.scrollTop = optionEl.offsetTop - dropdownEl.offsetTop - 1;
        }
      });
    }
  }

  writeValue(value: any) {
    this.value = value;

    if (!!this._value) {
      const formattedDateTime = this.formatSelectedDateTime(this._value);
      this.calendarInputValue = formattedDateTime;
    }
    this.changeDetector.markForCheck();
  }

  registerOnChange(onChange: any) {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any) {
    this.onTouched = onTouched;
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  setCurrentCalendarDateFromValue(value: Date) {
    const month = value.getMonth();
    const year = value.getFullYear();
    this.currentCalendarDate = new Date(year, month, 1);
  }

  setSelectedCalendarDateFromValue(value: Date) {
    this.selectedCalendarDate = new Date(value.getTime());
    this.selectedCalendarDate.setHours(0, 0, 0, 0);
  }

  nextMonth() {
    const month = this.currentCalendarDate.getMonth();
    const year = this.currentCalendarDate.getFullYear();
    this.currentCalendarDate = new Date(year, month + 1, 1);
    this.buildDateMatrix();
    this.changeDetector.markForCheck();
  }

  previousMonth() {
    const month = this.currentCalendarDate.getMonth();
    const year = this.currentCalendarDate.getFullYear();
    this.currentCalendarDate = new Date(year, month - 1, 1);
    this.buildDateMatrix();
    this.changeDetector.markForCheck();
  }

  textInputChange(value: string) {
    const parsedDate = moment(value);
    if (parsedDate.isValid()) {
      const newDate = parsedDate.toDate();
      this.markAsTouched();
      this.onChange(newDate);
      this.value = newDate;
      this.valueChanged.emit(this.value);
    } else if (this.allowEmptyDateString && !value?.trim()) {
      this.markAsTouched();
      this.onChange('');
      this.value = '';
      this.valueChanged.emit(this.value);
    }
    this.changeDetector.markForCheck();
  }

  hoursChange() {
    const isPm = this._value.getHours() >= 12;
    let newHours = this.amPmHour;
    if (isPm && this.amPmHour < 12) {
      newHours += 12;
    } else {
      if (this.amPmHour === 12) {
        newHours = 0;
      }
    }

    this._value?.setHours(newHours);
    this.selectDate(this._value);
  }

  minutesChange() {
    this._value?.setMinutes(this.minutes);
    this.selectDate(this._value);
  }

  selectAm() {
    let hour = this._value.getHours();
    if (hour >= 12) {
      hour = hour - 12;
      this._value?.setHours(hour);
    }
    this.selectDate(this._value);
    this.changeDetector.markForCheck();
  }

  selectPm() {
    let hour = this._value.getHours();
    if (hour < 12) {
      hour = hour + 12;
      this._value?.setHours(hour);
    }
    this.selectDate(this._value);
    this.changeDetector.markForCheck();
  }

  selectDate(value: Date) {
    // get the time from the current value
    // selecting a date should not remove the existing
    // selected time
    const hours = this._value?.getHours() || 0;
    const minutes = this._value?.getMinutes() || 0;
    const newDate = new Date(value.getTime());
    newDate.setHours(hours, minutes, 0);

    const val = this.formatSelectedDateTime(newDate);
    this.calendarInputValue = val;

    if (!this.showTime) {
      this.showCalendar = false;
    }

    this.markAsTouched();
    this.onChange(newDate);
    this.value = newDate;
    this.valueChanged.emit(this.value);
    this.changeDetector.markForCheck();
    this.ignoreClick = true;
  }

  formatSelectedDateTime(dateToFormat: Date) {
    const day = ('0' + dateToFormat.getDate()).slice(-2);
    const month = ('0' + (dateToFormat.getMonth() + 1)).slice(-2);
    const year = dateToFormat.getFullYear();
    let hour = dateToFormat.getHours();
    const minute = ('0' + dateToFormat.getMinutes()).slice(-2);
    var am = hour >= 12 ? 'PM' : 'AM';
    if (hour > 12) {
      hour = hour - 12;
    }
    if (hour === 0) {
      hour = 12;
    }
    const hourDisplay = ('0' + hour).slice(-2);

    if (this.translateService.currentLang === 'en-US') {
      if (this.showTime) {
        return `${month}/${day}/${year} ${hourDisplay}:${minute} ${am}`;
      } else {
        return `${month}/${day}/${year}`;
      }
    } else {
      const m = moment(dateToFormat);
      if (this.showTime) {
        return m.format(this.translateService.instant('FORMAT.LONG-DATE-WITH-TIME'));
      } else {
        return m.format(this.translateService.instant('FORMAT.DATE-ONLY'));
      }
    }
  }

  buildDateMatrix() {
    const monthDate = new Date(this.currentCalendarDate.getTime());
    const month = monthDate.getMonth();
    const year = monthDate.getFullYear();
    const monthFirstDayOfWeek = monthDate.getDay();
    //const startCalendarOffsetDays = 7 - monthFirstDayOfWeek;
    const startCalendarOffsetDays = monthFirstDayOfWeek;
    var currentDate = monthDate.getDate() - startCalendarOffsetDays;

    const endDate = new Date(monthDate.setMonth(monthDate.getMonth() + 1));

    this.dateMatrix = [[]];
    let day = 0;
    let wk = 0;
    while (new Date(year, month, currentDate).getTime() < endDate.getTime()) {
      this.dateMatrix[wk][day] = new Date(year, month, currentDate);

      currentDate = currentDate + 1;

      day++;

      if (day === 7) {
        day = 0;
        wk++;
        this.dateMatrix[wk] = [];
      }
    }

    // populate last week if needed
    while (day <= 6) {
      this.dateMatrix[wk][day] = new Date(year, month, currentDate);
      currentDate = currentDate + 1;
      day++;
    }

    this.monthDisplay = this.monthLabels[month];
    this.yearDisplay = year;
  }

  selectYear(year: number): void {
    const newDate = new Date(this._value);
    newDate.setFullYear(year);

    this.showCalendar = false;
    this.markAsTouched();
    this.onChange(newDate);
    this.value = newDate;
    this.valueChanged.emit(this.value);
    this.changeDetector.markForCheck();
    this.ignoreClick = true;
  }
}
